// ===================================================================
// SCENARIO 1: ASKING FOR CLARITY
// Managing Upward Sprint - Surviving Level
// ===================================================================

export const scenario1 = {
  id: 1,
  title: "Asking for Clarity",
  level: "surviving",
  duration: 90,
  durationText: "90 sec",
  icon: "💬",
  
  setup: {
    text: `You're a junior analyst at IndiaFirst Bank. It's 4 PM on a Tuesday, and you're wrapping up a client report when your manager Priya sends you this Slack message:

"Hey, can you update the client deck and send it EOD? Thanks!"

You stare at the message. Client deck? You've been working on three different client presentations this week. Which one does she mean? And what needs updating—new data, revised recommendations, just formatting? EOD could mean 5 PM or 7 PM depending on who you ask.`,
    audioUrl: null // Audio narration not implemented in MVP
  },
  
  challenge: "The instruction is vague. Which client? Which deck? What needs updating? What time is EOD exactly?",
  
  validation: "This happens ALL THE TIME. Senior people assume you have context you don't have. They're not being difficult—they're just moving fast and juggling multiple things. Your job: get the clarity you need without seeming clueless or high-maintenance.",
  
  rubric: {
    clarity: {
      excellent: "Asks 3+ specific, multiple-choice questions that are easy to answer",
      good: "Asks 2-3 specific questions",
      okay: "Asks 1 specific question or 2+ vague questions",
      poor: "Asks only vague questions",
      fail: "Doesn't ask clarifying questions or completely misses the point"
    },
    respect: {
      excellent: "Professional tone, one brief softener (optional), no over-apologizing",
      good: "Polite, minimal apologies (1-2)",
      okay: "Multiple apologies (3+) but still respectful",
      poor: "Either too deferential (excessive apologies) OR too casual",
      fail: "Disrespectful, condescending, or unprofessional"
    },
    solutionFocus: {
      excellent: "Offers multiple-choice questions or proposes next steps",
      good: "Asks clear yes/no questions that are easy to answer quickly",
      okay: "Asks open-ended questions only (harder for manager to answer)",
      poor: "Focuses on the problem without seeking clarity",
      fail: "Just complains or expresses frustration"
    },
    assertiveness: {
      excellent: "Direct, confident, no hedging ('I need clarity on...')",
      good: "Mostly direct with minor hedging ('I wanted to check...')",
      okay: "Some confidence but lots of hedging ('I was just wondering if maybe...')",
      poor: "Very tentative, excessive hedging throughout",
      fail: "Either passive (too apologetic) OR aggressive (demanding tone)"
    }
  },
  
  alternatives: [
    {
      type: "passive",
      title: "Too Passive ❌",
      response: `Hi Priya,

I'm really sorry to bother you with this, but I just wanted to check if maybe you could clarify which deck you meant? I don't want to mess this up or anything. 

Sorry again for the extra back-and-forth!

Thanks so much,
[Your name]`,
      score: {
        clarity: 2,
        respect: 2,
        solutionFocus: 2,
        assertiveness: 1
      },
      feedback: `This has TOO many apologies (3 times in a short message!). You're not "bothering" her—you're being professional by asking for clarity.

The question is also vague: "which deck you meant?" doesn't give Priya options to choose from. She has to think harder to answer you.

**Why the low assertiveness score:** Phrases like "just wanted to check if maybe" and "or anything" signal excessive uncertainty. You sound like you're afraid to ask a legitimate question.

**Research insight:** Studies show that excessive apologizing in professional contexts actually reduces perceived competence, especially for women and junior employees.`
    },
    {
      type: "aggressive",
      title: "Too Aggressive ❌",
      response: `Which deck? There are like 5 client decks I've been working on this week.

And EOD is ambiguous—do you mean 5 PM or 7 PM?

Need more details to prioritize this.`,
      score: {
        clarity: 4,
        respect: 2,
        solutionFocus: 3,
        assertiveness: 5
      },
      feedback: `Good specificity—you're asking about the right things (which deck, EOD timing). But the tone feels frustrated or even slightly accusatory.

**The problem:** Starting with "Which deck?" (no greeting, no context) sounds abrupt. "There are like 5 client decks" comes across as complaining about your workload.

**Why respect score is low:** While directness is good, you can be clear WITHOUT sounding irritated. Remember: Priya's not being unclear on purpose. She's probably juggling 10 things.

**How to fix it:** Same questions, warmer framing. "Quick clarification so I can prioritize..." positions you as solution-oriented, not frustrated.`
    },
    {
      type: "assertive",
      title: "Assertive (Goldilocks Zone) ✅",
      response: `Hi Priya,

Quick clarification to make sure I deliver what you need:

1. Which client deck? (Project Falcon / Client X Proposal / Monthly Performance Update?)
2. What should I update? (New data from this week / Revised recommendations / Formatting changes?)
3. EOD timing? (5 PM / 7 PM / Tomorrow morning works too?)

Happy to prioritize this—just want to confirm the details so I get it right.

Thanks!
[Your name]`,
      score: {
        clarity: 5,
        respect: 5,
        solutionFocus: 5,
        assertiveness: 5
      },
      feedback: `🎯 **EXCELLENT.** This hits the assertive zone perfectly.

**Why clarity is 5/5:** Three specific questions with multiple-choice options. Priya can respond in 30 seconds: "Falcon / New data / 7 PM." Easy for her, clear for you.

**Why respect is 5/5:** One softener ("Quick clarification") sets a collaborative tone without over-apologizing. Professional throughout.

**Why solution-focus is 5/5:** "To make sure I deliver what you need" frames this as you caring about quality, not you being confused. "Happy to prioritize" shows ownership.

**Why assertiveness is 5/5:** Direct, confident, no hedging. You're not asking permission to ask—you're efficiently gathering info you need to do good work.

**The principle (from research):** Asking specific questions with multiple-choice options shows competence, not weakness. You're demonstrating that you understand what good work requires.

**Pro tip:** Notice there's zero apologies. That's intentional. Asking for clarity isn't a mistake—it's professionalism. Save apologies for actual errors.`
    }
  ],
  
  reflection: {
    question: "On a scale of 1-5, how comfortable are you currently asking for clarity when instructions are unclear?",
    responses: {
      low: "That's completely normal. Most junior analysts worry that asking questions makes them seem incompetent. Here's what research shows: Managers respect people who ask specific questions MORE than people who guess and get it wrong. You're building this skill.",
      medium: "You're in the middle—sometimes you ask, sometimes you don't. The goal is to recognize that asking for clarity is ALWAYS the right move. It's not about confidence in yourself; it's about respect for the work and the person you're working with.",
      high: "Great! You already know asking questions is smart. Voice Sprint will help you refine HOW you ask—making it even easier for busy managers to give you what you need quickly with multiple-choice questions instead of open-ended ones."
    }
  },
  
  badges: ["first_pushback", "clarity_champion"],
  xpReward: 100,
  nextScenarioId: null // Only one scenario in MVP
};
